import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowRight, MessageCircle } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function FAQ() {
  const { t } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();
  const items = t("faq.items", { returnObjects: true }) || [];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Medical Tourism FAQ | Questions About Turkey Healthcare Answers | Monimek</title>
        <meta name="description" content="Is medical tourism safe? Do doctors speak English? Read our frequently asked questions about getting medical, dental, or surgical treatments in Turkey." />
        <link rel="canonical" href="https://www.monizmek.com/faq" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Is medical tourism in Turkey safe?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. Turkey has one of the highest numbers of JCI-accredited hospitals in the world, ensuring safety and quality standards that match or exceed those in the US."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How much can I save compared to the USA or Canada?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Most patients save 50–75% on treatments. For example, a hair transplant that costs $15,000–$25,000 in the USA can be done for $2,000–$5,500 in Turkey with the same or better quality. Dental work and cosmetic procedures offer similar savings."
                  }
                }
              ]
            }
          `}
        </script>
      </Helmet>
      {/* Hero */}
      <section className="page-hero text-white">
        <div className="max-w-[1200px] mx-auto px-6 py-12 text-center">
          <span className="overline-text text-[#3FA7D6]">{t("faq.overline")}</span>
          <h1 className="text-4xl sm:text-5xl font-black text-white font-outfit tracking-tight mt-3 mb-4" data-testid="faq-hero-title">
            {t("faq.title")}
          </h1>
          <p className="text-white/75 text-lg max-w-xl mx-auto">{t("faq.subtitle")}</p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-[#F8FAFC]" ref={ref}>
        <div className="max-w-[800px] mx-auto px-6">
          <div className={`anim-init ${isVisible ? "in-view" : ""}`}>
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {items.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="bg-white rounded-2xl border border-gray-100 px-6 shadow-[0_2px_10px_rgba(0,0,0,0.03)] data-[state=open]:shadow-[0_4px_20px_rgba(63,167,214,0.12)] data-[state=open]:border-[#3FA7D6]/30 transition-shadow"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-[#0B3C5D] font-semibold text-left hover:no-underline py-5 hover:text-[#3FA7D6] transition-colors text-base">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#6B7280] leading-relaxed pb-5 text-sm">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Still have questions */}
          <div className={`mt-12 text-center anim-init stagger-2 ${isVisible ? "in-view" : ""}`} data-testid="faq-contact-section">
            <div className="bg-[#0B3C5D] rounded-3xl p-10">
              <div className="w-14 h-14 rounded-full bg-[#3FA7D6]/20 flex items-center justify-center mx-auto mb-5">
                <MessageCircle className="w-7 h-7 text-[#3FA7D6]" />
              </div>
              <h3 className="text-2xl font-bold text-white font-outfit mb-3">{t("faq.stillHaveQuestions")}</h3>
              <p className="text-white/65 mb-7">{t("contact.infoText")}</p>
              <Link
                to="/contact"
                data-testid="faq-contact-cta"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#3FA7D6] text-white font-bold rounded-full hover:bg-white hover:text-[#0B3C5D] transition-all duration-200 hover:scale-105"
              >
                {t("faq.contactUs")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
